import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  AppBar,
  Toolbar,
 
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  
  useMediaQuery,
  useTheme,
  Button,
  IconButton,
  Popover,
  
  Divider,
  Paper,
  
  Dialog,
  DialogContent,
  DialogTitle,
  Tabs,
  Tab,
} from "@mui/material";
import {
  Pets,

  Person,
  FilterList,
  Close,
  
  Visibility,
} from "@mui/icons-material";
import { URL } from "../constants/url";
import colors from "../constants/colors";

// Reusable Animal Image Component with better error handling
const AnimalImage = ({ src, alt, fallback, ...props }) => {
  const [imgSrc, setImgSrc] = useState("");
  const [hasError, setHasError] = useState(false);

  // Function to get complete image URL
  const getImageUrl = (imagePath) => {
    if (!imagePath) return "";
    if (imagePath.startsWith("http")) return imagePath;
    return `${URL}${imagePath}`;
  };

  useEffect(() => {
    setImgSrc(getImageUrl(src));
    setHasError(false);
  }, [src]);

  const handleError = () => {
    if (!hasError) {
      setHasError(true);
      setImgSrc(
        fallback ||
          "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjYwIiBoZWlnaHQ9IjYwIiBmaWxsPSIjRjBGMEYwIi8+CjxwYXRoIGQ9Ik0zMCAzN0MzMi43NjE0IDM3IDM1IDM0Ljc2MTQgMzUgMzJDMzUgMjkuMjM4NiAzMi43NjE0IDI3IDMwIDI3QzI3LjIzODYgMjcgMjUgMjkuMjM4NiAyNSAzMkMyNSAzNC43NjE0IDI3LjIzODYgMzcgMzAgMzdaIiBmaWxsPSIjQ0VDRUNFIi8+CjxwYXRoIGQ9Ik0zNiAzNEw0MiAyOEw0OCAzNEw0MiA0MEwzNiAzNFoiIGZpbGw9IiNDRUNFQ0UiLz4KPC9zdmc+"
      );
    }
  };

  return <img src={imgSrc} alt={alt} onError={handleError} {...props} />;
};

const CowshedAnimalsAdmin = ({ adminData, searchText }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [animalData, setAnimalData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Filter states
  const [categoryFilter, setCategoryFilter] = useState("");
  const [subcategoryFilter, setSubcategoryFilter] = useState("");
  const [userFilter, setUserFilter] = useState("");

  // Filter popover state
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);

  // Modal state
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const showAlert = (message, severity = "success") => {
    setAlert({ open: true, message, severity });
  };

  const handleCloseAlert = () => {
    setAlert({ open: false, message: "", severity: "success" });
  };

  // Fetch all cowshed animals data
  const fetchCowshedAnimals = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${URL}/api/dairy/cowshed-animals`);
      const data = await res.json();

      if (data.success) {
        setAnimalData(data.data || []);
      } else {
        showAlert(data.message || "Error fetching animal data", "error");
      }
    } catch (error) {
      console.error("Error fetching cowshed animals:", error);
      showAlert("Error fetching animal data", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCowshedAnimals();
  }, []);

  const { allCategories, allSubcategories, allUsers } = useMemo(() => {
    const categories = new Set();
    const subcategories = new Set();
    const users = new Set();

    animalData.forEach((user) => {
      users.add(user.userName);
      user.categories.forEach((category) => {
        categories.add(category.name);
        category.subcategories.forEach((subcat) => {
          subcategories.add(subcat.name);
        });
      });
    });

    return {
      allCategories: Array.from(categories).sort(),
      allSubcategories: Array.from(subcategories).sort(),
      allUsers: Array.from(users).sort(),
    };
  }, [animalData]);

  // Get filtered subcategories based on selected category
  const filteredSubcategories = useMemo(() => {
    if (!categoryFilter) return [];

    const filteredSubs = new Set();
    animalData.forEach((user) => {
      user.categories.forEach((category) => {
        if (category.name === categoryFilter) {
          category.subcategories.forEach((subcat) => {
            filteredSubs.add(subcat.name);
          });
        }
      });
    });

    return Array.from(filteredSubs).sort();
  }, [animalData, categoryFilter]);

  // Filter data based on selected filters
  const filteredData = useMemo(() => {
    return animalData.filter((user) => {
      // Filter by user name
      if (userFilter && user.userName !== userFilter) return false;

      // Filter by category and subcategory
      const hasMatchingCategory = user.categories.some((category) => {
        if (categoryFilter && category.name !== categoryFilter) return false;

        if (subcategoryFilter) {
          return category.subcategories.some(
            (subcat) => subcat.name === subcategoryFilter
          );
        }

        return true;
      });

      return hasMatchingCategory;
    });
  }, [animalData, categoryFilter, subcategoryFilter, userFilter]);

  // Calculate total statistics
  const totalStats = useMemo(() => {
    let totalAnimals = 0;

    filteredData.forEach((user) => {
      totalAnimals += user.totalAnimals;
    });

    return {
      totalUsers: filteredData.length,
      totalAnimals: totalAnimals,
    };
  }, [filteredData]);

  // Filter handlers
  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleResetFilters = () => {
    setCategoryFilter("");
    setSubcategoryFilter("");
    setUserFilter("");
  };

  const applyFilters = () => {
    handleFilterClose();
  };

  // Modal handlers
  const handleViewDetails = (userData) => {
    setSelectedUser(userData);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedUser(null);
  };

  const filterOpen = Boolean(filterAnchorEl);
  const filterId = filterOpen ? "filter-popover" : undefined;

  // Check if any filter is active
  const isFilterActive = categoryFilter || subcategoryFilter || userFilter;

  // Custom Alert Component
  const CustomAlert = ({ open, severity, message, onClose }) => {
    if (!open) return null;

    return (
      <Box
        sx={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 9999,
          width: "auto",
          minWidth: { xs: "90vw", sm: 400 },
          maxWidth: "90vw",
        }}
      >
        <Box
          sx={{
            backgroundColor: "white",
            border: `3px solid ${
              severity === "success" ? colors.limeGreen : colors.deleteBg
            }`,
            borderRadius: 4,
            padding: 0,
            boxShadow: "0 10px 40px rgba(0,0,0,0.25)",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              backgroundColor:
                severity === "success" ? colors.limeGreen : colors.deleteBg,
              padding: { xs: "12px 16px", sm: "16px 24px" },
              textAlign: "center",
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: "1.1rem", sm: "1.3rem" },
                fontWeight: 700,
                color: "white",
                textTransform: "uppercase",
              }}
            >
              {severity === "success" ? "Success" : "Error"}
            </Typography>
          </Box>
          <Box sx={{ padding: { xs: "16px", sm: "24px" } }}>
            <Typography
              sx={{
                fontSize: { xs: "1rem", sm: "1.1rem" },
                fontWeight: 500,
                textAlign: "center",
                mb: 2,
              }}
            >
              {message}
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Button
                variant="contained"
                onClick={onClose}
                sx={{
                  backgroundColor:
                    severity === "success" ? colors.darkGreen : colors.red,
                  color: "white",
                  fontWeight: 700,
                  borderRadius: 3,
                  px: 4,
                  "&:hover": {
                    backgroundColor:
                      severity === "success" ? colors.green : colors.darkRed,
                  },
                }}
              >
                OK
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  // ✅ Clean, Modern & Properly Structured User Detail Modal
  const UserDetailModal = ({ userData, open, onClose }) => {
    if (!userData) return null;

    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 0,
            overflow: "hidden",
          },
        }}
      >
        {/* Header */}
        <DialogTitle
          sx={{
            backgroundColor: colors.green,
            color: "white",
            py: 2,
            px: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            {userData.userName}'s Animals
          </Typography>
          <IconButton onClick={onClose} sx={{ color: "white" }}>
            <Close />
          </IconButton>
        </DialogTitle>

        {/* Content */}
        <DialogContent sx={{ p: 3, maxHeight: "75vh", overflowY: "auto" }}>
          {/* USER INFORMATION */}
          <Box sx={{ mb: 2, mt: 1 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
              User Details
            </Typography>
            <Divider sx={{ mb: 1, borderColor: colors.green }} />

            <Typography variant="body2" sx={{ py: 0.5 }}>
              <strong>Email:</strong> {userData.userEmail || "—"}
            </Typography>

            <Typography variant="body2" sx={{ py: 0.5 }}>
              <strong>Phone:</strong> {userData.userPhone || "—"}
            </Typography>

            <Typography variant="body2" sx={{ py: 0.5 }}>
              <strong>Address:</strong> {userData.userAddress || "—"}
            </Typography>
          </Box>

          <Divider sx={{ my: 2, borderColor: colors.green }} />

          {/* ANIMAL SUMMARY */}
          <Box
            sx={{
              display: "flex",
              gap: 3,
              flexDirection: { xs: "column", sm: "row" },
              alignItems: "center",
              mb: 2,
            }}
          >
            <Box
              sx={{
                textAlign: "center",
                flex: 1,
                backgroundColor: colors.lightGreen,
                borderRadius: 2,
                py: 2.3,
                px: 1,
              }}
            >
              <Typography
                variant="h4"
                fontWeight={700}
                color={colors.darkGreen}
              >
                {userData.totalAnimals}
              </Typography>
              <Typography
                variant="body2"
                fontWeight={500}
                color={colors.darkGreen}
              >
                Total Animals
              </Typography>
            </Box>
          </Box>

          {userData.categories?.length > 0 ? (
            userData.categories.map((cat, i) => (
              <Box
                key={i}
                sx={{
                  mb: 3,
                  p: 2,
                  borderRadius: 3,
                  border: `1px solid ${colors.lightGray}`,
                  boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
                  backgroundColor: "#fff",
                }}
              >
                {/* CATEGORY NAME + TOTAL */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    fontWeight={600}
                    color={colors.darkGreen}
                  >
                    {cat.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total: <strong>{cat.totalCount}</strong> animals
                  </Typography>
                </Box>

                {/* SUBCATEGORIES BELOW */}
                {cat.subcategories?.length > 0 ? (
                  <Grid container spacing={2}>
                    {cat.subcategories.map((sub, j) => (
                      <Grid item xs={12} sm={6} md={4} key={j}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            p: 1.2,
                            backgroundColor:
                              j % 2 === 0 ? "#fafafa" : "#f2f7f2",
                            borderRadius: 2,
                            border: `1px solid ${colors.lightGray}`,
                          }}
                        >
                          {/* SUBCATEGORY IMAGE */}
                          <Box
                            sx={{
                              width: 55,
                              height: 55,
                              borderRadius: 2,
                              overflow: "hidden",
                              border: `1px solid ${colors.midGray}`,
                              flexShrink: 0,
                            }}
                          >
                            {sub.sampleImages?.[0] ? (
                              <AnimalImage
                                src={sub.sampleImages[0]}
                                alt={sub.name}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                            ) : (
                              <Box
                                sx={{
                                  width: "100%",
                                  height: "100%",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  backgroundColor: "#f7f7f7",
                                }}
                              >
                                <Pets
                                  sx={{ fontSize: 28, color: colors.midGray }}
                                />
                              </Box>
                            )}
                          </Box>

                          {/* SUBCATEGORY NAME + COUNT */}
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="body2" fontWeight={600}>
                              {sub.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color={colors.darkGreen}
                              fontWeight={500}
                            >
                              Count: {sub.count}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      textAlign: "center",
                      py: 1,
                      fontStyle: "italic",
                    }}
                  >
                    No animals in this category
                  </Typography>
                )}
              </Box>
            ))
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center", py: 2 }}
            >
              No animals registered
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: colors.lightGray,
        overflow: "hidden",
      }}
    >
      {/* Custom Alert */}
      <CustomAlert
        open={alert.open}
        severity={alert.severity}
        message={alert.message}
        onClose={handleCloseAlert}
      />

      {/* User Detail Modal */}
      <UserDetailModal
        userData={selectedUser}
        open={modalOpen}
        onClose={handleCloseModal}
      />

      {/* AppBar with Statistics */}
      <AppBar
        position="sticky"
        sx={{
          bgcolor: "white",
          color: colors.darkBlack,
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
          zIndex: 10,
        }}
      >
        <Toolbar
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
            minHeight: "64px !important",
            px: { xs: 2, sm: 3 },
            flexWrap: "wrap",
            gap: { xs: 1.5, sm: 2 },
          }}
        >
          {/* Tabs Section */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              display: "flex",
              justifyContent: { xs: "center", sm: "flex-start" },
            }}
          >
            <Tabs
              value={0}
              sx={{
                minHeight: "48px",
                "& .MuiTabs-indicator": {
                  backgroundColor: colors.green,
                  height: 3,
                  borderRadius: 3,
                },
                "& .MuiTab-root": {
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: { xs: "0.8rem", sm: "0.9rem", md: "1rem" },
                  minWidth: "auto",
                  px: { xs: 1.5, sm: 3 },
                  minHeight: "48px",
                },
                "& .MuiTab-root.Mui-selected": {
                  color: colors.green,
                },
              }}
            >
              <Tab
                icon={
                  <Pets sx={{ mr: 1, fontSize: { xs: "18px", sm: "20px" } }} />
                }
                iconPosition="start"
                label={isMobile ? "Animals" : "All Cowshed Animals"}
              />
            </Tabs>
          </Box>

          {/* Stats + Filter Button */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 1, sm: 2 },
              flexShrink: 0,
            }}
          >
            {/* Stats Cards */}
            <Card
              sx={{
                textAlign: "center",
                p: 0.8,
                minWidth: { xs: 50, sm: 70 },
                backgroundColor: colors.white,
                borderRadius: 2,
                boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                border: `1.7px solid ${colors.darkGreen}`,
              }}
            >
              <Typography
                variant="h6"
                fontWeight="800"
                color={colors.darkBlack}
                lineHeight={1.1}
              >
                {totalStats.totalUsers}
              </Typography>
              <Typography
                variant="body2"
                fontWeight="600"
                color={colors.darkBlack}
                fontSize="0.75rem"
              >
                Users
              </Typography>
            </Card>

            <Card
              sx={{
                textAlign: "center",
                p: 0.8,
                minWidth: { xs: 50, sm: 70 },
                backgroundColor: colors.white,
                borderRadius: 2,
                boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                border: `1.7px solid ${colors.darkGreen}`,
              }}
            >
              <Typography
                variant="h6"
                fontWeight="800"
                color={colors.darkBlack}
                lineHeight={1.1}
              >
                {totalStats.totalAnimals}
              </Typography>
              <Typography
                variant="body2"
                fontWeight="600"
                color={colors.darkBlack}
                fontSize="0.75rem"
              >
                Animals
              </Typography>
            </Card>

            {/* Filter Button */}
            <Button
              variant="contained"
              startIcon={<FilterList />}
              onClick={handleFilterClick}
              sx={{
                background: colors.darkGreen,
                "&:hover": { background: colors.green },
                fontSize: "0.875rem",
                px: 2.5,
                py: 0.9,
                borderRadius: 3,
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
            >
              Filter
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Filter Popover */}
      {/* <Popover
        id={filterId}
        open={filterOpen}
        anchorEl={filterAnchorEl}
        onClose={handleFilterClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        sx={{ mt: 1 }}
      >
        <Paper sx={{ p: 3, width: 320 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6" fontWeight="600">
              Filters
            </Typography>
            <IconButton size="small" onClick={handleFilterClose}>
              <Close />
            </IconButton>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Filter by User</InputLabel>
              <Select
                value={userFilter}
                label="Filter by User"
                onChange={(e) => setUserFilter(e.target.value)}
              >
                <MenuItem value="">All Users</MenuItem>
                {allUsers.map((user) => (
                  <MenuItem key={user} value={user}>
                    {user}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel>Filter by Category</InputLabel>
              <Select
                value={categoryFilter}
                label="Filter by Category"
                onChange={(e) => {
                  setCategoryFilter(e.target.value);
                  setSubcategoryFilter("");
                }}
              >
                <MenuItem value="">All Categories</MenuItem>
                {allCategories.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small" disabled={!categoryFilter}>
              <InputLabel>Filter by Subcategory</InputLabel>
              <Select
                value={subcategoryFilter}
                label="Filter by Subcategory"
                onChange={(e) => setSubcategoryFilter(e.target.value)}
              >
                <MenuItem value="">All Subcategories</MenuItem>
                {filteredSubcategories.map((subcat) => (
                  <MenuItem key={subcat} value={subcat}>
                    {subcat}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
              <Button
                onClick={handleResetFilters}
                variant="outlined"
                fullWidth
                sx={{
                  borderColor: colors.green,
                  color: colors.green,
                  fontWeight: 600,
                }}
              >
                Reset
              </Button>
              <Button
                onClick={applyFilters}
                variant="contained"
                fullWidth
                sx={{
                  backgroundColor: colors.green,
                  fontWeight: 600,
                }}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Paper>
      </Popover> */}
{/* Filter Popover */}
<Popover
  id={filterId}
  open={filterOpen}
  anchorEl={filterAnchorEl}
  onClose={handleFilterClose}
  anchorOrigin={{
    vertical: "bottom",
    horizontal: "right",
  }}
  transformOrigin={{
    vertical: "top",
    horizontal: "right",
  }}
  sx={{ mt: 1 }}
>
  <Paper 
    sx={{ 
      p: 3, 
      width: 320,
      maxHeight: 500, // Fixed maximum height
      display: "flex",
      flexDirection: "column",
    }}
  >
    {/* Header */}
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mb: 2,
        flexShrink: 0, // Prevent header from shrinking
      }}
    >
      <Typography variant="h6" fontWeight="600">
        Filters
      </Typography>
      <IconButton size="small" onClick={handleFilterClose}>
        <Close />
      </IconButton>
    </Box>

    {/* Scrollable Content */}
    <Box 
      sx={{ 
        display: "flex", 
        flexDirection: "column", 
        gap: 2,
        flexGrow: 1,
        overflow: "auto", // Enable scrolling
        "&::-webkit-scrollbar": {
          width: "6px",
        },
        "&::-webkit-scrollbar-track": {
          background: "#f1f1f1",
          borderRadius: "3px",
        },
        "&::-webkit-scrollbar-thumb": {
          background: "#c1c1c1",
          borderRadius: "3px",
          "&:hover": {
            background: "#a8a8a8",
          }
        },
      }}
    >
      <FormControl fullWidth size="small">
        <InputLabel>Filter by User</InputLabel>
        <Select
          value={userFilter}
          label="Filter by User"
          onChange={(e) => setUserFilter(e.target.value)}
          MenuProps={{
            PaperProps: {
              sx: {
                maxHeight: 250, // Fixed height for dropdown menu
                "& .MuiMenuItem-root": {
                  minHeight: "36px", // Fixed menu item height
                }
              }
            }
          }}
        >
          <MenuItem value="">All Users</MenuItem>
          {allUsers.map((user) => (
            <MenuItem key={user} value={user}>
              {user}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth size="small">
        <InputLabel>Filter by Category</InputLabel>
        <Select
          value={categoryFilter}
          label="Filter by Category"
          onChange={(e) => {
            setCategoryFilter(e.target.value);
            setSubcategoryFilter("");
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                maxHeight: 250, // Fixed height for dropdown menu
                "& .MuiMenuItem-root": {
                  minHeight: "36px", // Fixed menu item height
                }
              }
            }
          }}
        >
          <MenuItem value="">All Categories</MenuItem>
          {allCategories.map((category) => (
            <MenuItem key={category} value={category}>
              {category}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth size="small" disabled={!categoryFilter}>
        <InputLabel>Filter by Subcategory</InputLabel>
        <Select
          value={subcategoryFilter}
          label="Filter by Subcategory"
          onChange={(e) => setSubcategoryFilter(e.target.value)}
          MenuProps={{
            PaperProps: {
              sx: {
                maxHeight: 250, // Fixed height for dropdown menu
                "& .MuiMenuItem-root": {
                  minHeight: "36px", // Fixed menu item height
                }
              }
            }
          }}
        >
          <MenuItem value="">All Subcategories</MenuItem>
          {filteredSubcategories.map((subcat) => (
            <MenuItem key={subcat} value={subcat}>
              {subcat}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>

    {/* Fixed Buttons at Bottom */}
    <Box 
      sx={{ 
        display: "flex", 
        gap: 1, 
        mt: 2,
        pt: 2,
        borderTop: "1px solid #eee",
        flexShrink: 0, // Prevent buttons from shrinking
      }}
    >
      <Button
        onClick={handleResetFilters}
        variant="outlined"
        fullWidth
        sx={{
          borderColor: colors.green,
          color: colors.green,
          fontWeight: 600,
        }}
      >
        Reset
      </Button>
      <Button
        onClick={applyFilters}
        variant="contained"
        fullWidth
        sx={{
          backgroundColor: colors.green,
          fontWeight: 600,
        }}
      >
        Apply
      </Button>
    </Box>
  </Paper>
</Popover>
      <Box
        sx={{
          flexGrow: 1,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Users List */}
        <Box
          sx={{
            flexGrow: 1,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            p: { xs: 2, sm: 3 },
          }}
        >
          {/* Filter Status with Chips */}
          {isFilterActive && (
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {userFilter && (
                <Chip
                  label={`User: ${userFilter}`}
                  size="small"
                  onDelete={() => setUserFilter('')}
                  deleteIcon={<Close />}
                  sx={{
                    backgroundColor: colors.lightGreen,
                    color: colors.darkGreen,
                    fontWeight: 500,
                    '& .MuiChip-deleteIcon': {
                      color: colors.darkGreen,
                      '&:hover': {
                        color: colors.green,
                      }
                    }
                  }}
                />
              )}
              {categoryFilter && (
                <Chip
                  label={`Category: ${categoryFilter}`}
                  size="small"
                  onDelete={() => {
                    setCategoryFilter('');
                    setSubcategoryFilter('');
                  }}
                  deleteIcon={<Close />}
                  sx={{
                    backgroundColor: colors.lightGreen,
                    color: colors.darkGreen,
                    fontWeight: 500,
                    '& .MuiChip-deleteIcon': {
                      color: colors.darkGreen,
                      '&:hover': {
                        color: colors.green,
                      }
                    }
                  }}
                />
              )}
              {subcategoryFilter && (
                <Chip
                  label={`Subcategory: ${subcategoryFilter}`}
                  size="small"
                  onDelete={() => setSubcategoryFilter('')}
                  deleteIcon={<Close />}
                  sx={{
                    backgroundColor: colors.lightGreen,
                    color: colors.darkGreen,
                    fontWeight: 500,
                    '& .MuiChip-deleteIcon': {
                      color: colors.darkGreen,
                      '&:hover': {
                        color: colors.green,
                      }
                    }
                  }}
                />
              )}
              <Button
                size="small"
                onClick={handleResetFilters}
                sx={{
                  color: colors.green,
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  textTransform: 'none'
                }}
              >
                Clear All
              </Button>
            </Box>
          )}

          {/* Loading State */}
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 6,
              }}
            >
              <CircularProgress size={46} sx={{ color: colors.green }} />
            </Box>
          ) : (
            <Box
              sx={{
                flexGrow: 1,
                overflowY: "auto",
                "&::-webkit-scrollbar": { display: "none" },
                msOverflowStyle: "none",
                scrollbarWidth: "none",
                px: { xs: 1, sm: 2 },
                pb: 2,
              }}
            >
              {filteredData.length > 0 ? (
                filteredData.map((user) => (
                  <Card
                    key={user.userId}
                    sx={{
                      mb: 1.4,
                      borderRadius: 2.5,
                      border: `1px solid ${colors.lightGray}`,
                      backgroundColor: "#fff",
                      boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
                      transition: "all 0.25s ease",
                      "&:hover": {
                        boxShadow: "0 4px 14px rgba(0,0,0,0.10)",
                        borderColor: colors.green,
                      },
                    }}
                  >
                    <CardContent sx={{ p: 1.8 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          flexWrap: "wrap",
                          gap: 2,
                        }}
                      >
                        {/* USER INFO */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            flex: 1,
                            minWidth: 0,
                          }}
                        >
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: "50%",
                              backgroundColor: colors.lightGreen,
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              mr: 1.5,
                            }}
                          >
                            <Person
                              sx={{ color: colors.darkGreen, fontSize: 22 }}
                            />
                          </Box>

                          <Box sx={{ overflow: "hidden" }}>
                            <Typography
                              variant="subtitle1"
                              fontWeight={600}
                              color={colors.darkBlack}
                              noWrap
                            >
                              {user.userName}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ color: "#7c7c7c", fontSize: "0.82rem" }}
                            >
                              {user.userEmail}
                            </Typography>
                          </Box>
                        </Box>

                        {/* ANIMAL COUNT */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            background: colors.lightGreen,
                            px: 1.4,
                            py: 0.5,
                            borderRadius: 2,
                            gap: 0.6,
                            flexShrink: 0,
                          }}
                        >
                          <Pets
                            sx={{ color: colors.darkGreen, fontSize: 20 }}
                          />
                          <Typography
                            variant="h6"
                            fontWeight={700}
                            color={colors.darkGreen}
                          >
                            {user.totalAnimals}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 500, color: colors.darkGreen }}
                          >
                            Animals
                          </Typography>
                        </Box>

                        {/* VIEW BTN */}
                        <Button
                          variant="contained"
                          startIcon={<Visibility />}
                          onClick={() => handleViewDetails(user)}
                          sx={{
                            backgroundColor: colors.green,
                            color: "#fff",
                            borderRadius: 2,
                            px: 2,
                            py: 0.6,
                            fontSize: "0.85rem",
                            textTransform: "none",
                            fontWeight: 600,
                            "&:hover": {
                              backgroundColor: colors.darkGreen,
                            },
                          }}
                        >
                          View Details
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <Card
                  sx={{
                    textAlign: "center",
                    py: 5,
                    borderRadius: 2,
                    background: "#fff",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                  }}
                >
                  <Typography
                    variant="h6"
                    color={colors.darkBlack}
                    sx={{ mb: 0.4 }}
                  >
                    No Users Found
                  </Typography>
                  <Typography variant="body2" color="#7c7c7c">
                    {animalData.length === 0
                      ? "No user data available"
                      : "Try adjusting your filters"}
                  </Typography>
                </Card>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default CowshedAnimalsAdmin;